import { fNumber } from 'common/utils/formatNumber'
import {
  useUpdateNotificationSettingProfile,
  useUpdateNotificationSettingThreshold,
} from 'modules/notification/api/mutation'
import { useGetNotificationsSettingsByModule } from 'modules/notification/api/queries'
import { NotificationItemsType, NotificationSettingData } from 'modules/notification/api/types'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Card, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import NotificationSettingCard from '../NotificationSettingCard'
import LoadingSkeleton from '../notificationCard/LoadingSkeleton'

interface SubModule {
  key: string
  title: string
  description?: string
  unit?: string
  sort?: number
  value?: {
    key: string
    name: string
    unit?: string
  }[]
}

const tabValues = ['water_quality', 'food', 'mortality', 'cost'] as const

// ---------------- TabPanel ----------------
const NotificationTabPanel = ({
  children,
  index,
  value,
}: {
  children: React.ReactNode
  index: number
  value: number
}) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
)

// ---------------- Main Component ----------------
export default function NotificationSettingTab() {
  const { t } = useTranslation(['notification', 'landing', 'pondComparison', 'water', 'cost'])
  const theme = useTheme()
  const [value, setValue] = React.useState(0)

  const { mutate: updateNotificationSettingThreshold } = useUpdateNotificationSettingThreshold(
    tabValues[value]
  )
  const { mutate: updateNotificationSettingProfile } = useUpdateNotificationSettingProfile(
    tabValues[value]
  )

  // ---------------- Stable callbacks ----------------
  const updateThreshold = React.useCallback(
    (id: string, payload?: Record<string, any>) => {
      if (!id) return
      updateNotificationSettingThreshold(
        { id, payload },
        {
          onError: (err) => console.error('Failed to update threshold:', err),
        }
      )
    },
    [updateNotificationSettingThreshold]
  )

  const updateProfile = React.useCallback(
    (id: string, payload?: Record<string, any>) => {
      if (!id) return
      updateNotificationSettingProfile(
        { id, payload },
        {
          onError: (err) => console.error('Failed to update profile:', err),
        }
      )
    },
    [updateNotificationSettingProfile]
  )

  // ---------------- SubModules ----------------
  const subModulesConfig: Record<string, SubModule> = React.useMemo(
    () => ({
      ph: {
        key: 'ph',
        sort: 1.1,
        title: t('pondComparison:ph_value'),
        description: `${t('notification:min')} $min ${t('notification:max')} $max`,
        value: [
          { key: 'min', name: t('notification:min.value') },
          { key: 'max', name: t('notification:max.value') },
        ],
      },
      do: {
        key: 'do',
        sort: 1.2,
        title: t('pondComparison:do_value'),
        description: `${t('notification:min')} $minppm ${t('notification:max')} $maxppm`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: 'ppm' },
          { key: 'max', name: t('notification:max.value'), unit: 'ppm' },
        ],
      },
      alkali: {
        key: 'alkali',
        sort: 1.6,
        title: `${t('water:value')}${t('water:alkali')}`,
        description: `${t('notification:min')} $minppm ${t('notification:max')} $maxppm`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: 'ppm' },
          { key: 'max', name: t('notification:max.value'), unit: 'ppm' },
        ],
      },
      ammonia: {
        key: 'ammonia',
        sort: 1.4,
        title: `${t('water:value')}${t('water:ammonia')}`,
        description: `${t('notification:min')} $minppm ${t('notification:max')} $maxppm`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: 'ppm' },
          { key: 'max', name: t('notification:max.value'), unit: 'ppm' },
        ],
      },
      nitrite: {
        key: 'nitrite',
        sort: 1.5,
        title: `${t('water:value')}${t('water:nitrite')}`,
        description: `${t('notification:min')} $minppm ${t('notification:max')} $maxppm`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: 'ppm' },
          { key: 'max', name: t('notification:max.value'), unit: 'ppm' },
        ],
      },
      temperature: {
        key: 'temperature',
        sort: 1.3,
        title: `${t('water:value')}${t('water:temperature')}`,
        description: `${t('notification:min')} $min°C ${t('notification:max')} $max°C`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: '°C' },
          { key: 'max', name: t('notification:max.value'), unit: '°C' },
        ],
      },
      turbidity: {
        key: 'turbidity',
        sort: 1.7,
        title: `${t('water:value')}${t('water:turbidity')}`,
        description: `${t('notification:min')} $mincm ${t('notification:max')} $maxcm`,
        value: [
          { key: 'min', name: t('notification:min.value'), unit: 'cm' },
          { key: 'max', name: t('notification:max.value'), unit: 'cm' },
        ],
      },
      meal_consumption_trend_down: {
        key: 'meal_consumption_trend_down',
        sort: 2.1,
        title: t('notification:meal.down'),
        description: `${t('notification:feed.decrease')} $percent_decrease% ${t(
          'notification:consecutive'
        )} $consecutive_days ${t('pondComparison:unit.day')}`,
        value: [
          { key: 'percent_decrease', name: t('notification:trend.down'), unit: '%' },
          {
            key: 'consecutive_days',
            name: t('notification:consecutive.day'),
            unit: t('pondComparison:unit.day'),
          },
        ],
      },
      meal_leftover_high: {
        key: 'meal_leftover_high',
        sort: 2.2,
        title: t('notification:meal.left'),
        description: `${t('notification:meal.left.exceed')} $max_percent% ${t(
          'notification:feed.given'
        )}`,
        value: [{ key: 'max_percent', name: t('notification:food.left'), unit: '%' }],
      },
      mortality_trend_up: {
        key: 'mortality_trend_up',
        sort: 3.1,
        title: t('notification:mortality'),
        description: `${t('notification:dead.shrimp.increase')} $percent_increase% ${t(
          'notification:consecutive'
        )} $consecutive_days ${t('pondComparison:unit.day')}`,
        value: [
          { key: 'percent_increase', name: t('notification:dead.amount'), unit: '%' },
          {
            key: 'consecutive_days',
            name: t('notification:consecutive.day'),
            unit: t('pondComparison:unit.day'),
          },
        ],
      },
      cost_budget_variance_high: {
        key: 'cost_budget_variance_high',
        sort: 4.1,
        title: t('notification:cost'),
        description: `${t('cost:cost.title')} ${t('notification:exceed')} $budget_amount ${t(
          'pondComparison:unit.baht'
        )} $variance_percent %`,
        value: [
          { key: 'budget_amount', name: t('notification:cost'), unit: '฿' },
          { key: 'variance_percent', name: t('notification:cost.exceed'), unit: '%' },
        ],
      },
      cost_per_kg_high: {
        key: 'cost_per_kg_high',
        sort: 4.2,
        title: t('notification:cost.kg'),
        description: `${t('notification:cost.exceed.kg')} $max_amount ${t(
          'pondComparison:unit.baht'
        )}`,
        value: [{ key: 'max_amount', name: t('notification:cost.exceed.kg'), unit: '฿' }],
      },
    }),
    [t]
  )

  // ---------------- API data ----------------
  const {
    data: notiSettingsData,
    isFetching,
    isLoading,
    refetch,
  } = useGetNotificationsSettingsByModule(tabValues[value])

  const tabs = React.useMemo(
    () => [
      { id: 'all', name: t('landing:water.quality') },
      { id: 'food', name: t('landing:food') },
      { id: 'shrimp_dead', name: t('landing:dead.shrimp') },
      { id: 'cost', name: t('landing:cost') },
    ],
    [t]
  )

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    refetch()
  }

  // ---------------- Notification Item ----------------
  const NotificationItem = React.useCallback(
    ({ notification }: { notification: NotificationSettingData }) => {
      const subModuleConfig = subModulesConfig[notification.sub_module_code]
      if (!subModuleConfig) return null

      const configValues = subModuleConfig.value ?? []
      const subModuleValuesWithCurrent = configValues.map((v) => ({
        ...v,
        value: notification.thresholds?.[v.key] ?? '',
      }))

      let description = subModuleConfig.description ?? ''

      return (
        <Grid item xs={12} sm={6} md={value === 0 ? 4 : 6}>
          <NotificationSettingCard
            id={notification.id}
            title={subModuleConfig.title}
            description={description}
            subModuleValues={subModuleValuesWithCurrent}
            enabled={notification.is_enabled}
            updateThreshold={(id, params) => updateThreshold(id ?? '', params)}
            updateProfile={(id, params) => updateProfile(id ?? '', params)}
          />
        </Grid>
      )
    },
    [subModulesConfig, value, updateThreshold, updateProfile]
  )

  const renderFlatNotifications = React.useCallback(() => {
    const currentTab = tabValues[value]
    const listObj = notiSettingsData?.data?.[currentTab]

    if (!listObj || Object.keys(listObj).length === 0) return null

    const list = Object.entries(listObj)
      .map(([key, notification]) => ({ key, ...notification }))
      .sort((a, b) => {
        const sortA = subModulesConfig[a.sub_module_code]?.sort ?? 9999
        const sortB = subModulesConfig[b.sub_module_code]?.sort ?? 9999
        return sortA - sortB
      })

    return list.map((n) => <NotificationItem key={n.id} notification={n} />)
  }, [notiSettingsData, subModulesConfig, value, NotificationItem])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab, idx) => (
            <Tab
              key={tab.id ?? idx}
              label={tab.name}
              sx={{
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Card sx={{ px: 4, py: 2, mt: 2, mb: 4 }}>
        {tabs.map((tt, i) => (
          <NotificationTabPanel key={tt.id ?? i} index={i} value={value}>
            <Typography mb={4} fontSize={18} fontWeight={600}>
              {[
                t('notification:water.title'),
                t('notification:food.title'),
                t('notification:deadshrimp.title'),
                t('notification:cost.title'),
              ][value] ?? ''}
            </Typography>
            <Grid container spacing={2}>
              {isLoading ? (
                <Grid item xs={12}>
                  <LoadingSkeleton />
                </Grid>
              ) : (
                renderFlatNotifications()
              )}
            </Grid>
          </NotificationTabPanel>
        ))}
      </Card>
    </Box>
  )
}
